import { oc } from "@orpc/contract";
import { z } from "zod";
import { BackgroundJobStatus } from "@erp/shared-types";
import {
  PresignedUploadUrlInputSchema,
  PresignedUploadUrlResponseSchema,
} from "../shared/presignedUpload";

/**
 * Hubbell Feedback File generation (AIA-28), as a background run.
 *
 * One run takes a Results Workbook and up to nine Customer Sales Summaries,
 * all staged in blob storage by the browser through presigned URLs, and
 * produces one Feedback File per paired Division. The run is a background job:
 * `start` answers with the job, `getLatestRun` is polled for progress and the
 * run report, and `getDivisionDownloadUrl` signs a short-lived link per tab
 * once the run has succeeded. At most one run is active per organization; a
 * new start replaces the previous run's files.
 *
 * The run report schemas live here rather than in the backend because the
 * report is recorded on the job's payload and read back by the page: both
 * sides validate the same shape.
 */

/**
 * Recognising the inputs. The page classifies a dropped file from its sheet
 * names and first rows before uploading it; the run reads it in full on the
 * server. Both decide from the same headers.
 */

/** The results header: a sheet of the Results Workbook whose first row carries all of these is a Division tab. */
export const HUBBELL_FEEDBACK_RESULTS_HEADER = [
  "Record ID",
  "LOAD NUM",
  "Title",
  "QUOTE SPA",
  "Account",
  "Group",
  "FINAL PRIOR MARGINS",
  "FINAL PRIOR COST ADDER",
  "COST INCREASE",
  "Division",
] as const;

/** A Customer Sales Summary's membership tab: its first row carries both. */
export const HUBBELL_FEEDBACK_MEMBERSHIP_HEADER = [
  "ACCOUNT",
  "LOADED TO GROUP",
] as const;

/** The Hubbell vendor column of the sales tab, found by text: its position differs per Division. */
export const HUBBELL_FEEDBACK_SALES_VENDOR_HEADER = "HUBBELL CANADA INC.";

/** A Customer Sales Summary's sales tab: its header row (the second, under a title) carries both. */
export const HUBBELL_FEEDBACK_SALES_HEADER = [
  "Account",
  HUBBELL_FEEDBACK_SALES_VENDOR_HEADER,
] as const;

/**
 * The suppliers whose results come back for a Feedback run. The flow, the
 * rule and the Feedback File are the same for each; what differs is the
 * Results Workbook's layout and column names, and which vendor column of the
 * (shared) Customer Sales Summary holds the supplier's sales.
 */
export const FEEDBACK_SUPPLIERS = ["hubbell", "abb"] as const;
export const FeedbackSupplierSchema = z.enum(FEEDBACK_SUPPLIERS);
export type FeedbackSupplier = z.infer<typeof FeedbackSupplierSchema>;

/** The Results Workbook columns the rule reads whose header differs by supplier. */
export type FeedbackResultsColumns = {
  loadNum: string;
  title: string;
  priorCostAdder: string;
};

export type FeedbackSupplierProfile = {
  supplier: FeedbackSupplier;
  /** As the Feedback File's name and the card's title write it. */
  name: string;
  /** How the pricing team names the Results Workbook on the card. */
  resultsFileLabel: string;
  /**
   * `tabs`: one sheet per Division, named by its tab letter (Hubbell's
   * FINAL.xlsx). `divisionColumn`: every Division on one sheet, each line's
   * tab letter in its Division column (ABB).
   */
  resultsLayout: "tabs" | "divisionColumn";
  /** Every header a results sheet carries; recognises the workbook. */
  resultsHeader: readonly string[];
  /** How many rows from the top the results header may sit on (ABB puts a UoM row above it). */
  resultsHeaderRows: number;
  columns: FeedbackResultsColumns;
  /** The supplier's vendor column on the sales tab, found by text. */
  salesVendorHeader: string;
};

export const FEEDBACK_SUPPLIER_PROFILES: Readonly<
  Record<FeedbackSupplier, FeedbackSupplierProfile>
> = {
  hubbell: {
    supplier: "hubbell",
    name: "Hubbell",
    resultsFileLabel: "FINAL.xlsx",
    resultsLayout: "tabs",
    resultsHeader: HUBBELL_FEEDBACK_RESULTS_HEADER,
    resultsHeaderRows: 1,
    columns: {
      loadNum: "LOAD NUM",
      title: "Title",
      priorCostAdder: "FINAL PRIOR COST ADDER",
    },
    salesVendorHeader: HUBBELL_FEEDBACK_SALES_VENDOR_HEADER,
  },
  abb: {
    supplier: "abb",
    name: "ABB",
    resultsFileLabel: "ALL - RESULTS - ABB",
    resultsLayout: "divisionColumn",
    // "ALL - RESULTS - ABB 11-1-2026.xlsx": one sheet, row 1 marks the UoM,
    // Prev Cost and New Cost columns, row 2 is the header.
    resultsHeader: [
      "Division",
      "NEW LOAD NUM",
      "UNIQUE TITLE",
      "QUOTE SPA",
      "Account",
      "Group",
      "FINAL PRIOR MARGINS",
      "FINAL PRIOR COST ADD%",
      "COST INCREASE",
    ],
    resultsHeaderRows: 3,
    columns: {
      loadNum: "NEW LOAD NUM",
      title: "UNIQUE TITLE",
      priorCostAdder: "FINAL PRIOR COST ADD%",
    },
    // ABB's Rexel vendor in the AS400 export (the THS items on ABB's SPAs).
    salesVendorHeader: "THOMAS & BETTS LIMITED",
  },
};

/** The sales tab's header for `supplier`: Account and the supplier's vendor column. */
export function feedbackSalesHeader(
  profile: FeedbackSupplierProfile,
): readonly [string, string] {
  return ["Account", profile.salesVendorHeader];
}

export type HubbellFeedbackDivision = {
  /** The Division's name as the pricing team writes it. */
  name: string;
  /** Its sheet name in the Results Workbook. */
  letter: string;
  /**
   * The two-character Division code the pricing team uses: a company letter
   * (Q, W or R) before the tab letter. The Feedback File's sheet is named
   * "<code> Feedback" (AIA-48).
   */
  code: string;
  /** Whether the AS400 export exists for this Division. */
  hasSalesFile: boolean;
};

/**
 * The Division table the pricing team supplied (docs/pricing/CONTEXT.md,
 * "Division tab"): each Rexel Division's tab letter in the Results Workbook
 * and the name its Customer Sales Summary file starts with. Rexel Utility has
 * a tab but no sales file: it is built only when the run is told to, with Sales 0 on every Load (AIA-49). The page lists
 * every tab in this order while a run goes.
 */
export const HUBBELL_FEEDBACK_DIVISIONS: readonly HubbellFeedbackDivision[] = [
  { name: "N. Quebec", letter: "O", code: "QO", hasSalesFile: true },
  { name: "N. West", letter: "T", code: "WT", hasSalesFile: true },
  { name: "N. Ontario", letter: "R", code: "WR", hasSalesFile: true },
  { name: "R. Atlantic", letter: "A", code: "RA", hasSalesFile: true },
  { name: "W. Midwest", letter: "B", code: "WB", hasSalesFile: true },
  { name: "W. Quebec", letter: "E", code: "QE", hasSalesFile: true },
  { name: "W. West", letter: "J", code: "WJ", hasSalesFile: true },
  { name: "W. Ontario", letter: "+", code: "WG", hasSalesFile: true },
  { name: "Rexel Utility", letter: "H", code: "RH", hasSalesFile: false },
];

/** Case and whitespace do not distinguish Divisions: "N.Quebec" is "N. Quebec". */
function normalizeDivisionName(name: string): string {
  return name.replace(/\s+/g, "").toLowerCase();
}

const DIVISION_BY_NORMALIZED_NAME = new Map(
  HUBBELL_FEEDBACK_DIVISIONS.map((division) => [
    normalizeDivisionName(division.name),
    division,
  ]),
);

/**
 * The Division a Customer Sales Summary file names: the file name before
 * " - " ("N.Quebec - AS400 Customer Summary 261001.xlsx"), or the whole base
 * name when there is no " - ". Undefined when it names no Division. Shared so
 * the page places a dropped file under its Division the way the run pairs it.
 */
export function hubbellFeedbackDivisionFromFileName(
  fileName: string,
): HubbellFeedbackDivision | undefined {
  const base = fileName.replace(/^.*[\\/]/, "").replace(/\.[^.]+$/, "");
  const separator = base.indexOf(" - ");
  const named = separator >= 0 ? base.slice(0, separator) : base;
  return DIVISION_BY_NORMALIZED_NAME.get(normalizeDivisionName(named));
}

/** Why a Division tab was not built. Client-neutral wording; the UI translates. */
export const HUBBELL_FEEDBACK_SKIP_REASONS = [
  "no sales file",
  "two sales files name this Division",
  "pairing mismatch",
  "tab not in the Division table",
  "tab not in the Results Workbook",
] as const;
export type HubbellFeedbackSkipReason =
  (typeof HUBBELL_FEEDBACK_SKIP_REASONS)[number];

/** Why a sales file paired with no Division tab. */
export const HUBBELL_FEEDBACK_UNPAIRED_REASONS = [
  "names no Division",
  "names a Division another file also names",
  "names a Division that has no sales file",
  "names a Division whose tab is not in the Results Workbook",
] as const;
export type HubbellFeedbackUnpairedReason =
  (typeof HUBBELL_FEEDBACK_UNPAIRED_REASONS)[number];

export const HubbellFeedbackBuiltDivisionSchema = z.object({
  status: z.literal("built"),
  tab: z.string(),
  division: z.string(),
  /** null for a Division that has no sales file (Rexel Utility): built with Sales 0. */
  salesFileName: z.string().nullable(),
  /** Share of the tab's Loads found in the sales file; null when there is none. */
  share: z.number().nullable(),
  loadCount: z.number().int(),
  unmatchedCount: z.number().int(),
  storageKey: z.string(),
  /** `<Supplier> Feedback <Division> <yyyy-mm-dd>.xlsx`, applied when a link is signed. */
  fileName: z.string(),
});
export type HubbellFeedbackBuiltDivision = z.infer<
  typeof HubbellFeedbackBuiltDivisionSchema
>;

export const HubbellFeedbackSkippedDivisionSchema = z.object({
  status: z.literal("skipped"),
  tab: z.string(),
  /** null for a tab the Division table does not know. */
  division: z.string().nullable(),
  reason: z.enum(HUBBELL_FEEDBACK_SKIP_REASONS),
  /** The file that named this Division, when the skip is about it (a mismatch). */
  salesFileName: z.string().nullable(),
  share: z.number().nullable(),
});
export type HubbellFeedbackSkippedDivision = z.infer<
  typeof HubbellFeedbackSkippedDivisionSchema
>;

export const HubbellFeedbackDivisionOutcomeSchema = z.discriminatedUnion(
  "status",
  [HubbellFeedbackBuiltDivisionSchema, HubbellFeedbackSkippedDivisionSchema],
);
export type HubbellFeedbackDivisionOutcome = z.infer<
  typeof HubbellFeedbackDivisionOutcomeSchema
>;

export const HubbellFeedbackUnpairedFileSchema = z.object({
  fileName: z.string(),
  reason: z.enum(HUBBELL_FEEDBACK_UNPAIRED_REASONS),
});
export type HubbellFeedbackUnpairedFile = z.infer<
  typeof HubbellFeedbackUnpairedFileSchema
>;

export const HubbellFeedbackRunReportSchema = z.object({
  divisions: z.array(HubbellFeedbackDivisionOutcomeSchema),
  unpairedFiles: z.array(HubbellFeedbackUnpairedFileSchema),
  /** Bucket / container the outputs were written to; null for the storage default. */
  container: z.string().nullable(),
});
export type HubbellFeedbackRunReport = z.infer<
  typeof HubbellFeedbackRunReportSchema
>;

/** A staged upload the run reads: the key `getUploadUrl` minted, and the file's own name. */
export const HubbellFeedbackStagedFileSchema = z.object({
  uploadKey: z.string().min(1),
  fileName: z.string().min(1).max(255),
});
export type HubbellFeedbackStagedFile = z.infer<
  typeof HubbellFeedbackStagedFileSchema
>;

/** The Division table has nine tabs; a drop never has more sales files than that. */
export const HUBBELL_FEEDBACK_MAX_SALES_FILES = 9;

export const StartHubbellFeedbackRunInputSchema = z.object({
  /** Whose results the run reads; Hubbell for callers from before ABB. */
  supplier: FeedbackSupplierSchema.default("hubbell"),
  resultsWorkbook: HubbellFeedbackStagedFileSchema,
  /** The Divisions to build from their Customer Sales Summary. */
  salesFiles: z
    .array(HubbellFeedbackStagedFileSchema)
    .max(HUBBELL_FEEDBACK_MAX_SALES_FILES),
  /** Tab letters of the Divisions to build with no sales file: Sales 0 on every Load (AIA-49). */
  noSalesFileTabs: z.array(z.string().min(1).max(4)).max(9).default([]),
});
export type StartHubbellFeedbackRunInput = z.infer<
  typeof StartHubbellFeedbackRunInputSchema
>;

/** The inputs a run was started with, as the page shows them (names only). */
export const HubbellFeedbackRunInputsSchema = z.object({
  resultsWorkbook: z.object({ fileName: z.string() }),
  salesFiles: z.array(z.object({ fileName: z.string() })),
  /** Divisions built with Sales 0, by tab letter. */
  noSalesFileTabs: z.array(z.string()).default([]),
});

/**
 * One run, from the organization's point of view: the job's state plus what
 * the run has reported so far. `progress` is every Division outcome known at
 * the time of the read (the run reports one per Division as it goes); `report`
 * is set once the run has succeeded and is the complete picture.
 */
export const HubbellFeedbackRunSchema = z.object({
  backgroundJobId: z.uuid(),
  supplier: FeedbackSupplierSchema,
  status: z.enum(BackgroundJobStatus),
  /** 0-100 while running, from Divisions reported over Divisions in the table; null before the first. */
  percent: z.number().nullable(),
  /** Safe to show verbatim; present only on failure. */
  errorMessage: z.string().nullable(),
  /**
   * Whether the caller started this run. The run is the organization's to
   * watch, but its downloads are the starter's (the job lookup behind them is
   * user-scoped), so the page offers them only when this is true.
   */
  startedByCurrentUser: z.boolean(),
  /**
   * When the Feedback Files stop being downloadable (the export retention,
   * counted from the run's completion); null until the run has succeeded.
   */
  downloadsExpireAt: z.date().nullable(),
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  inputs: HubbellFeedbackRunInputsSchema,
  progress: z.array(HubbellFeedbackDivisionOutcomeSchema),
  report: HubbellFeedbackRunReportSchema.nullable(),
});
export type HubbellFeedbackRun = z.infer<typeof HubbellFeedbackRunSchema>;

export const GetHubbellFeedbackDivisionDownloadUrlInputSchema = z.object({
  backgroundJobId: z.uuid(),
  /** The Division tab letter, as the report names it. */
  tab: z.string().min(1).max(4),
});

export const GetHubbellFeedbackDivisionDownloadUrlResponseSchema = z.object({
  /** Signed, short-lived (minutes), and downloads under `fileName`. */
  url: z.string(),
  fileName: z.string(),
});

export const hubbellFeedbackContracts = {
  /** Mint a presigned URL for the browser to PUT one workbook to (the Results Workbook or a sales file). */
  getUploadUrl: oc
    .input(PresignedUploadUrlInputSchema)
    .output(PresignedUploadUrlResponseSchema)
    .route({
      summary: "Get an upload URL for a Hubbell Feedback input workbook",
      description:
        "Returns a presigned URL for uploading the Results Workbook or one Customer Sales Summary (spreadsheets up to 100 MB) that a Hubbell Feedback run reads.",
    }),

  /**
   * Start a run on the staged inputs. Answers with the organization's active
   * run when one is already going rather than starting a second one.
   */
  start: oc
    .input(StartHubbellFeedbackRunInputSchema)
    .output(HubbellFeedbackRunSchema)
    .route({
      method: "POST",
      summary: "Start a Hubbell Feedback run",
      description:
        "Starts a background run that builds one Feedback File per Division from the staged Results Workbook and Customer Sales Summaries, replacing the previous run's files; returns the active run when one is already in progress.",
    }),

  /** The organization's most recent run for one supplier, or null before the first. */
  getLatestRun: oc
    .input(
      z.object({
        /** Each supplier has its own runs; Hubbell for callers from before ABB. */
        supplier: FeedbackSupplierSchema.default("hubbell"),
      }),
    )
    .output(z.object({ run: HubbellFeedbackRunSchema.nullable() }))
    .route({
      method: "GET",
      summary: "Get the latest Hubbell Feedback run",
      description:
        "Returns the organization's most recent Hubbell Feedback run with its status, per-Division progress and run report, or null before any run.",
    }),

  /** A short-lived link to one built Division's Feedback File. */
  getDivisionDownloadUrl: oc
    .input(GetHubbellFeedbackDivisionDownloadUrlInputSchema)
    .output(GetHubbellFeedbackDivisionDownloadUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Get a download link for one Division's Hubbell Feedback File",
      description:
        "Returns a temporary download link and file name for one Division tab of a finished Hubbell Feedback run the signed-in user started.",
    }),
} as const;
